import type { StackProps } from '@chakra-ui/react';
import { Flex, HStack, Image, Text, VStack } from '@chakra-ui/react';
import {
  BONDING_SUPPLY,
  FUN_TOKEN_DECIMALS,
  LAUNCH_TOKEN_FEE_AGENTFUN,
} from '@cronos-app/sdk';
import { MaskIcon } from '@ui/components/MaskIcon';
import { Num, Percent } from '@ui/components/Num';
import { UsdValue } from '@ui/components/Num/UsdValue';
import { Token } from '@ui/config/token';
import { useAgentLaunchReceiveAmount } from '@ui/hooks/bondingCurve/useAgentLaunchReceiveAmount';
import { useAgentFunBalance } from '@ui/hooks/contracts/useTokenBalance';
import { useTokenPrice } from '@ui/hooks/tokenPrice';
import { Fraction } from 'bi-fraction';
import { type FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import type { CreateAgentFormValues } from '../constants';
import { useCreateAgentModal } from '../useCreateAgentModal';
import { CreateAgentAmountInput } from './CreateAgentAmountInput';
import Subtitle from './Subtitle';

const CreateAgentPurchase: FC = () => {
  const step = useCreateAgentModal((s) => s.step);
  if (step !== 'Purchase') return null;

  return (
    <>
      <Subtitle mb={6}>
        Add initial tokens as a safety charm to protect from snipers (◍•ᴗ•◍)✧*。
      </Subtitle>
      <Flex direction="column" gap={1}>
        <CreateAgentAmountInput />
        <VStack gap={4} align="stretch">
          <Balance mt={2} />
          <YouWillReceive />
          <MaskIcon
            mx="auto"
            boxSize="24px"
            icon="url(/masks/convert-arrow.svg)"
            bg="primary"
          />
          <InitialBuy />
          <CreationFee />
        </VStack>
      </Flex>
    </>
  );
};

const InitialBuy: FC = () => {
  const enteredAmount = useCreateAgentModal((s) => s.buyAmount);
  const agentFunPrice = useTokenPrice({
    token: Token.agentFun,
  }).data?.usd_price;

  if (enteredAmount.lte(0)) return null;
  return (
    <HStack align="flex-start" textStyle="body2" justify="space-between">
      <Text>Initial buy</Text>
      <VStack align="flex-end" gap={1}>
        <HStack gap="4px" fontWeight="bold">
          <Num value={enteredAmount} />
          <Image alt="Agent FUN" src="/tokens/agentfun.svg" boxSize="16px" />
        </HStack>
        <UsdValue
          value={enteredAmount}
          price={agentFunPrice}
          textStyle="body3"
        />
      </VStack>
    </HStack>
  );
};

const YouWillReceive: FC = () => {
  const enteredAmount = useCreateAgentModal((s) => s.buyAmount);
  const values = useFormContext<CreateAgentFormValues>().getValues();
  const buyAmount = useCreateAgentModal((s) => s.buyAmount);
  const { data: receiveAmount } = useAgentLaunchReceiveAmount({
    purchaseAmount: buyAmount,
  });
  const ratioOfTotalSupply = useMemo(() => {
    const initialSupplyOfBondingToken = new Fraction(BONDING_SUPPLY).shr(
      FUN_TOKEN_DECIMALS,
    );
    return receiveAmount
      ? receiveAmount.div(initialSupplyOfBondingToken)
      : undefined;
  }, [receiveAmount]);

  if (enteredAmount.lte(0)) return null;
  return (
    <HStack align="flex-start" textStyle="body2" justify="space-between">
      <Text>You’ll receive</Text>
      <VStack align="flex-end" gap={1}>
        <HStack textStyle="body3Bold" gap="4px">
          <Num value={receiveAmount} />
          {values.image && (
            <Image
              boxSize="16px"
              rounded="full"
              src={URL.createObjectURL(values.image)}
              alt={values.ticker}
            />
          )}
        </HStack>
        <Percent
          prefix="("
          value={ratioOfTotalSupply}
          suffix="% of total supply)"
          textStyle="body3"
        />
      </VStack>
    </HStack>
  );
};

const CreationFee: FC = () => {
  const price = useTokenPrice({ token: Token.agentFun }).data?.usd_price;

  return (
    <HStack align="flex-start" textStyle="body2" justify="space-between">
      <Text>Creation Fee</Text>
      <VStack align="flex-end" gap={1}>
        <HStack gap={0.5} fontWeight="bold">
          <Num value={LAUNCH_TOKEN_FEE_AGENTFUN} />
          <Image alt="Agent FUN" src="/tokens/agentfun.svg" boxSize="16px" />
        </HStack>
        <Flex textStyle="body3">
          <Num
            display="inline"
            decimalPlaces={2}
            prefix="$"
            value={price?.mul(LAUNCH_TOKEN_FEE_AGENTFUN)}
          />
        </Flex>
      </VStack>
    </HStack>
  );
};

const Balance: FC<StackProps> = (props) => {
  const balance = useAgentFunBalance().data;
  const agentFunPrice = useTokenPrice({
    token: Token.agentFun,
  }).data?.usd_price;
  return (
    <HStack textStyle="body2" justify="space-between" {...props}>
      <Text>Balance</Text>
      <VStack gap="4px" alignItems="flex-end">
        <HStack gap="4px" fontWeight="bold">
          <Num value={balance} />
          <Image alt="Agent FUN" src="/tokens/agentfun.svg" boxSize="16px" />
        </HStack>
        <UsdValue value={balance} price={agentFunPrice} textStyle="body3" />
      </VStack>
    </HStack>
  );
};

export default CreateAgentPurchase;
